import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getBillFilterHeaderOnline, filterBillsListOnline, formatDateForApi } from '../../../utilities/api';

export const fetchAdvanceFilterOptions = createAsyncThunk(
    'advanceBillReports/fetchAdvanceFilterOptions',
    async ({ rolename, clientname }, { rejectWithValue }) => {
        try {
            const response = await getBillFilterHeaderOnline({ rolename, clientname });
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch filter options');
        }
    }
);

export const fetchAdvanceBillsData = createAsyncThunk(
    'advanceBillReports/fetchAdvanceBillsData',
    async ({ fromDate, toDate, branchId, status }, { rejectWithValue }) => {
        try {
            const resolvedBranchId = branchId || '';
            const payload = {
                from_date: fromDate ? formatDateForApi(fromDate) : '',
                to_date: toDate ? formatDateForApi(toDate) : '',
                branch_id: resolvedBranchId,
                status: status || ''
            };
            if (String(resolvedBranchId).toLowerCase() === 'all') {
                const clientId = localStorage.getItem('clientid');
                if (clientId) payload.client_id = clientId;
            }
            const data = await filterBillsListOnline(payload);
            return data;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch bills data');
        }
    }
);

const initialState = {
    branches: [],
    statusOptions: [],
    selectedBranch: null,
    selectedStatus: null,
    fromDate: null,
    toDate: null,
    billData: [],
    loading: false,
    filterOptionsLoading: false,
    error: null,
    filterOptionsError: null
};

const advanceBillReportsSlice = createSlice({
    name: 'advanceBillReports',
    initialState,
    reducers: {
        setFromDate: (state, action) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action) => {
            state.toDate = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
        },
        setSelectedStatus: (state, action) => {
            state.selectedStatus = action.payload;
        },
        setFilters: (state, action) => {
            const { fromDate, toDate, selectedBranch, selectedStatus } = action.payload;
            if (fromDate !== undefined) state.fromDate = fromDate;
            if (toDate !== undefined) state.toDate = toDate;
            if (selectedBranch !== undefined) state.selectedBranch = selectedBranch;
            if (selectedStatus !== undefined) state.selectedStatus = selectedStatus;
        },
        resetFilters: (state) => {
            state.fromDate = null;
            state.toDate = null;
            state.selectedBranch = null;
            state.selectedStatus = null;
            state.billData = [];
            state.error = null;
        },
        clearBillData: (state) => {
            state.billData = [];
        },
        clearError: (state) => {
            state.error = null;
            state.filterOptionsError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAdvanceFilterOptions.pending, (state) => {
                state.filterOptionsLoading = true;
                state.filterOptionsError = null;
            })
            .addCase(fetchAdvanceFilterOptions.fulfilled, (state, action) => {
                state.filterOptionsLoading = false;
                const rawBranches = Array.isArray(action.payload.branchs) ? action.payload.branchs : [];
                const hasAll = rawBranches.some((b) => String(b?.branch_id).toLowerCase() === 'all');
                state.branches = hasAll
                    ? rawBranches
                    : [{ branch_id: 'All', branch_name: 'All Branches' }, ...rawBranches];
                const statusOpts = [
                    { label: 'All Statuses', value: null },
                    ...(action.payload.status || []).map((item) => ({
                        label: item.status.charAt(0).toUpperCase() + item.status.slice(1),
                        value: item.status
                    }))
                ];
                state.statusOptions = statusOpts;
            })
            .addCase(fetchAdvanceFilterOptions.rejected, (state, action) => {
                state.filterOptionsLoading = false;
                state.filterOptionsError = action.payload;
                state.statusOptions = [{ label: 'All Statuses', value: null }];
            })
            .addCase(fetchAdvanceBillsData.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.billData = [];
            })
            .addCase(fetchAdvanceBillsData.fulfilled, (state, action) => {
                state.loading = false;
                state.billData = action.payload;
            })
            .addCase(fetchAdvanceBillsData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.billData = [];
            });
    }
});

export const { setFromDate, setToDate, setSelectedBranch, setSelectedStatus, setFilters, resetFilters, clearBillData, clearError } =
    advanceBillReportsSlice.actions;

export const selectBillData = (state) => state.advanceBillReports.billData;
export const selectLoading = (state) => state.advanceBillReports.loading;
export const selectBranches = (state) => state.advanceBillReports.branches;
export const selectStatusOptions = (state) => state.advanceBillReports.statusOptions;
export const selectSelectedBranch = (state) => state.advanceBillReports.selectedBranch;
export const selectSelectedStatus = (state) => state.advanceBillReports.selectedStatus;
export const selectFromDate = (state) => state.advanceBillReports.fromDate;
export const selectToDate = (state) => state.advanceBillReports.toDate;
export const selectError = (state) => state.advanceBillReports.error;

export default advanceBillReportsSlice.reducer;
